import { SpeedTestResult, HistoricalTestResult, LinkQuality } from 'shared-types';

// Numeric scores for grades
const gradeScores = { A: 5, B: 4, C: 3, D: 2, F: 1 };
type Grade = keyof typeof gradeScores;

// Grading thresholds customized for Pakistan's typical internet performance
// Lower is better for latency, higher is better for bandwidth
const thresholds = {
  ping: { a: 20, b: 50, c: 100, d: 200 }, // in ms
  jitter: { a: 5, b: 15, c: 30, d: 50 }, // in ms
  download: { a: 100, b: 50, c: 20, d: 5 }, // in Mbps
  upload: { a: 20, b: 10, c: 5, d: 1 }, // in Mbps
};

const getMetricGrade = (value: number, type: 'ping' | 'jitter' | 'download' | 'upload'): Grade => {
  const t = thresholds[type];
  const lowerIsBetter = type === 'ping' || type === 'jitter';
  
  if (lowerIsBetter) {
    if (value < t.a) return 'A';
    if (value < t.b) return 'B';
    if (value < t.c) return 'C';
    if (value < t.d) return 'D';
    return 'F';
  } else { // higher is better
    if (value > t.a) return 'A';
    if (value > t.b) return 'B';
    if (value > t.c) return 'C';
    if (value > t.d) return 'D';
    return 'F';
  }
};

const getQualityDetails = (grade: Grade): Omit<LinkQuality, 'score' | 'historyContext' | 'isError'> => {
  switch (grade) {
    case 'A': return { grade: 'A', title: 'Excellent', description: 'Your connection is superb! Perfect for 4K streaming, competitive gaming, and large file transfers.', color: 'text-green-500 dark:text-green-400' };
    case 'B': return { grade: 'B', title: 'Good', description: 'A solid connection for HD/4K streaming, online gaming, and video calls on multiple devices.', color: 'text-cyan-500 dark:text-cyan-400' };
    case 'C': return { grade: 'C', title: 'Fair', description: 'Suitable for web browsing and HD streaming. May struggle with more demanding tasks or multiple users.', color: 'text-yellow-500 dark:text-yellow-400' };
    case 'D': return { grade: 'D', title: 'Poor', description: 'Your connection may be unstable or slow. While browsing might be okay, you could face issues with streaming or gaming.', color: 'text-orange-500 dark:text-orange-400' };
    case 'F': default: return { grade: 'F', title: 'Very Poor', description: 'Your connection is very slow and likely unstable, which will impact most online activities.', color: 'text-red-500 dark:text-red-400' };
  }
};

const getHistoricalContext = (current: SpeedTestResult, history: HistoricalTestResult[]): string | undefined => {
    if (history.length < 3) return undefined; // Not enough data for a meaningful comparison

    const recentHistory = history.slice(0, 5); // Use last 5 tests
    const avgDownload = recentHistory.reduce((acc, h) => acc + h.download, 0) / recentHistory.length;
    
    if (avgDownload === 0) return undefined; // Avoid division by zero

    const difference = current.download - avgDownload;
    const percentageChange = (difference / avgDownload) * 100;

    if (percentageChange > 15) {
        return "Your speed is performing better than usual.";
    }
    if (percentageChange < -15) {
        return "This result is a bit slower than your recent average.";
    }
    return "Your connection is performing consistently.";
};

export const calculateLinkQuality = (results: SpeedTestResult, history?: HistoricalTestResult[]): LinkQuality => {
    if (results.download === 0 && results.ping === 0) {
        return getIncompleteTestQuality("Connection could not be fully measured. Please retest.");
    }

    const downloadScore = gradeScores[getMetricGrade(results.download, 'download')];
    const uploadScore = gradeScores[getMetricGrade(results.upload, 'upload')];
    const pingScore = gradeScores[getMetricGrade(results.ping, 'ping')];
    const jitterScore = gradeScores[getMetricGrade(results.jitter, 'jitter')];

    // Calculate a weighted average score.
    // Bandwidth (Download/Upload) is weighted more heavily (80%) than Latency (Ping/Jitter) (20%).
    const bandwidthScore = (downloadScore + uploadScore) / 2;
    const latencyScore = (pingScore + jitterScore) / 2;
    const finalScore = (bandwidthScore * 0.8) + (latencyScore * 0.2);

    const roundedFinalScore = Math.round(finalScore);
    const finalGrade = (Object.keys(gradeScores) as Grade[]).find(key => gradeScores[key] === roundedFinalScore) || 'F';
    const qualityDetails = getQualityDetails(finalGrade);

    const historyContext = history ? getHistoricalContext(results, history) : undefined;

    return {
        ...qualityDetails,
        score: finalScore * 20, // Convert 1-5 scale to 0-100 for the progress bar
        historyContext,
    };
};

export const getIncompleteTestQuality = (message: string): LinkQuality => ({
    grade: 'F',
    title: 'Test Incomplete',
    description: message,
    color: 'text-slate-500 dark:text-slate-400',
    score: 0,
    isError: true,
});

/**
 * Formats a speed from Mbps into the most appropriate unit (Kbps, Mbps, Gbps, etc.)
 * @param mbps - The speed in Megabits per second.
 * @returns An object with the formatted value string and the unit string.
 */
export const formatSpeed = (mbps: number): { value: string; unit: string } => {
    if (isNaN(mbps) || mbps < 0) {
        return { value: '0', unit: 'Mbps' };
    }
    if (mbps < 1) {
        return { value: (mbps * 1000).toFixed(0), unit: 'Kbps' };
    }

    let val = mbps;
    let unit = 'Mbps';
    const units = ['Gbps', 'Tbps', 'Pbps', 'Ebps'];
    let i = 0;
    while (val >= 1000 && i < units.length) {
        val /= 1000;
        unit = units[i];
        i++;
    }

    let decimals = 2;
    if (unit === 'Mbps') {
        if (val >= 100) decimals = 0;
        else if (val >= 10) decimals = 1;
    }
    
    return { value: val.toFixed(decimals), unit: unit };
};